import { alpha } from "@mui/material/styles";

function createGradient(color1: string, color2: string): string {
  return `linear-gradient(to left, ${color1}, ${color2})`;
}

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  brand: string;
}

declare module "@mui/material/styles" {
  interface TypeBackground {
    neutral: string;
    dim: string;
    default: string;
    secondary: string;
    surfaceTableHeader: string;
    pressedButton: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
    border: {
      light: string;
      main: string;
      dark: string;
      border1: string;
      borderMain: string;
      borderDark: string;
    };
    custom: {
      focused: string;
      hover: string;
      hover2: string;
    };
    pairs: Record<string, { name: string; primary: string; secondary: string }>;
    button: {
      nav: {
        backgroundColorActive: string;
        backgroundColorHover: string;
        color: string;
      };
      contained: {
        backgroundColorHover: string;
      };
      outlined: {
        backgroundColor: string;
        backgroundColorHover: string;
      };
    };
  }
  interface PaletteOptions {
    gradients?: GradientsPaletteOptions;
  }
}

declare module "@mui/material" {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

const GREY = {
  0: "#FFFFFF",
  100: "#F9FAFB",
  200: "#F4F6F8",
  300: "#DFE3E8",
  400: "#C4CDD5",
  500: "#919EAB",
  600: "#637381",
  700: "#454F5B",
  800: "#212B36",
  900: "#161C24",
  500_8: alpha("#919EAB", 0.08),
  500_12: alpha("#919EAB", 0.12),
  500_16: alpha("#919EAB", 0.16),
  500_24: alpha("#919EAB", 0.24),
  500_32: alpha("#919EAB", 0.32),
  500_48: alpha("#919EAB", 0.48),
  500_56: alpha("#919EAB", 0.56),
  500_80: alpha("#919EAB", 0.8),
};

// SETUP COLORS

const LIGHT_PRIMARY = {
  lighter: alpha("#3e4155", 0.5),
  light: alpha("#3e4155", 0.8),
  main: "#3e4155",
  dark: "#2a2d3a",
  darker: "#1e2130",
};

const DARK_PRIMARY = {
  lighter: alpha("#bfbfbf", 0.5),
  light: alpha("#bfbfbf", 0.8),
  main: "#bfbfbf",
  dark: "#a6a6a6",
  darker: "#8c8c8c",
};
const LIGHT_SECONDARY = {
  lighter: alpha("#666666", 0.5),
  light: alpha("#666666", 0.65),
  main: "#666666",
  dark: "#4d4d4d",
  darker: "#333333",
};
const DARK_SECONDARY = {
  lighter: alpha("#EDEDED", 0.65),
  light: alpha("#EDEDED", 0.65),
  main: alpha("#EDEDED", 0.65),
  dark: alpha("#EDEDED", 0.65),
  darker: alpha("#EDEDED", 0.65),
};
const INFO = {
  lighter: "#e9f1fb",
  light: "#6D89A9",
  main: "#010F05",
  dark: "#1976d2",
  darker: "#054166",
};
const SUCCESS = {
  lighter: "rgba(70,155,111,0.5)",
  light: "#5cc166",
  main: "#629C67",
  dark: "#629C67",
  darker: "#629C67",
};
const WARNING = {
  lighter: "#FFF7CD",
  light: "#FFE16A",
  main: "#FFC107",
  dark: "#A17C02",
  darker: "#7A4F01",
};
const ERROR = {
  lightest: "#f0d4d5",
  lighter: "#d08585",
  light: "#c46565",
  main: "#B74440",
  dark: "#8f3633",
  darker: "#6b2525",
};
const GRADIENTS = {
  primary: createGradient(LIGHT_PRIMARY.lighter, LIGHT_PRIMARY.main),
  secondary: createGradient(LIGHT_SECONDARY.lighter, LIGHT_SECONDARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

export const COLORS_PAIRS = {
  inputYellow: { name: "Input", primary: "#FFF2CC", secondary: "#A17C02" },
  outputGray: { name: "Output", primary: "#D9D9D9", secondary: "#566374" },
  subtotalGray: { name: "Subtotal", primary: "#F2F2F2", secondary: "#999999" },
  headerBlue: { name: "Header", primary: "#2F5597", secondary: "#1B3558" },
  errorRed: { name: "Error", primary: "#FFC7CE", secondary: "#C00000" },
  passGreen: { name: "Pass", primary: "#C6EFCE", secondary: "#00B050" },
  paleYellow: { name: "Pale Yellow", primary: "#FFF9E6", secondary: "#B8860B" },
  lightBlue: { name: "Light Blue", primary: "#E3F2FD", secondary: "#0563C1" },
  white: { name: "White", primary: "#FFFFFF", secondary: "#CCCCCC" },
  lightGreen: { name: "Light Green", primary: "#E6F4EA", secondary: "#34A853" },
  lightOrange: {
    name: "Light Orange",
    primary: "#FFE6CC",
    secondary: "#E65100",
  },
  lightPurple: {
    name: "Light Purple",
    primary: "#F3E5F5",
    secondary: "#6A1B9A",
  },
  red: { name: "Red", primary: "#F7DFDF", secondary: "#AA2C30" },
  orange: { name: "Orange", primary: "#FFF3E0", secondary: "#E65100" },
  goldenrod: { name: "Goldenrod", primary: "#FFFAEB", secondary: "#A17C02" },
  green: { name: "Green", primary: "#E6EFE8", secondary: "#62976c" },
  teal: { name: "Teal", primary: "#E0F2F1", secondary: "#00695C" },
  blue: { name: "Blue", primary: "#E3F2FD", secondary: "#1565C0" },
  indigo: { name: "Indigo", primary: "#E8EAF6", secondary: "#283593" },
  deepPurple: { name: "Deep purple", primary: "#EDE7F6", secondary: "#4527A0" },
  purple: { name: "Purple", primary: "#F3E5F5", secondary: "#6A1B9A" },
  pink: { name: "Pink", primary: "#FCE4EC", secondary: "#AD1457" },
  brown: { name: "Brown", primary: "#EFEBE9", secondary: "#4E342E" },
  gray: { name: "Gray", primary: "#EBECEC", secondary: "#566374" },
};

const COMMON = {
  common: { black: "#000", white: "#fff" },
  info: { ...INFO, contrastText: "#fff" },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: "#fff" },
  white: { main: "#fff", contrastText: GREY[800] },
  grey: GREY,
  offBlack: "#010F05",
  gradients: GRADIENTS,
  action: {
    hover: GREY[500_8],
    selected: "#010F05",
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: "#010F05",
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
  custom: {
    focused: "#010F05",
    hover: "#FAF9F7",
    hover2: "rgb(240, 242, 242)",
  },
  pairs: COLORS_PAIRS,
};
const palette = {
  light: {
    ...COMMON,
    primary: { ...LIGHT_PRIMARY, contrastText: "#fff" },
    secondary: { ...LIGHT_SECONDARY, contrastText: "#242424" },
    text: {
      primary: "#242424",
      secondary: "#666666",
      disabled: "#8c8c8c",
    },
    divider: "#d9d7d3",
    background: {
      default: "#f9f8f6",
      secondary: "#efedea",
      surfaceTableHeader: "#efedea",
      pressedButton: alpha("#3e4155", 0.16),
      paper: "#fdfcfb",
    },
    dataGrid: {
      backgroundColorRowHover: "#efedea",
      backgroundColorDisabled: "#efedea",
    },
    button: {
      nav: {
        backgroundColorActive: alpha("#3e4155", 0.12),
        backgroundColorHover: alpha("#3e4155", 0.06),
        color: "#242424",
      },
      contained: {
        backgroundColorHover: "#2a2d3a",
      },
      outlined: {
        backgroundColor: "#efedea",
        backgroundColorHover: "#d9d7d3",
      },
    },
    linkColor: "#3e4155",
    border: {
      borderMain: "#d9d7d3",
      borderDark: "#c4c1bc",
    },
    action: { ...COMMON.action },
    backdrop: {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
  },
  dark: {
    ...COMMON,
    mode: "dark",
    primary: { ...DARK_PRIMARY, contrastText: "#121212" },
    secondary: { ...DARK_SECONDARY, contrastText: DARK_PRIMARY.main },
    text: {
      primary: "#e8e8e8",
      secondary: "#969696",
      disabled: "#6b6b6b",
    },
    divider: "#333333",
    background: {
      default: "#121212",
      secondary: "#262626",
      surfaceTableHeader: "#262626",
      pressedButton: alpha("#8b8fa3", 0.16),
      paper: "#1a1a1a",
    },
    linkColor: "#8b8fa3",
    dataGrid: {
      backgroundColorRowHover: "#262626",
      backgroundColorDisabled: "#262626",
    },
    button: {
      nav: {
        backgroundColorActive: "#262626",
        backgroundColorHover: "#262626",
        color: "#e8e8e8",
      },
      contained: {
        backgroundColorHover: "#a6a6a6",
      },
      outlined: {
        backgroundColor: "#262626",
        backgroundColorHover: "#333333",
      },
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
      disabled: "#6b6b6b",
      disabledBackground: "#262626",
    },
    border: {
      borderMain: "#333333",
      borderDark: "#333333",
    },
    backdrop: {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
    },
  },
};

export default palette;
